/**
 * InstructorDetail Page
 *
 * Displays detailed information about a specific instructor
 * Only accessible to users with status='active'
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { getInstructor, type InstructorProfile } from '../../services/instructorService';
import { InstructorProfile as InstructorProfileComponent } from '../../components/instructor/InstructorProfile';

interface InstructorDetailProps {
  id?: string;
}

export function InstructorDetail({ id }: InstructorDetailProps) {
  const [instructor, setInstructor] = useState<InstructorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load instructor data
  useEffect(() => {
    if (!id) {
      setError('ID de instructor no especificado');
      setLoading(false);
      return;
    }

    const loadInstructor = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getInstructor(id);
        setInstructor(data);
      } catch (err: any) {
        console.error('[InstructorDetail] Error loading instructor:', err);
        setError(err.message || 'Error al cargar el perfil del instructor');
      } finally {
        setLoading(false);
      }
    };

    loadInstructor();
  }, [id]);

  // Navigate back to directory
  const goBack = () => {
    route('/');
  };

  // Loading state
  if (loading) {
    return (
      <div class="min-h-screen bg-gray-50 py-8">
        <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button Skeleton */}
          <div class="mb-6">
            <div class="h-10 w-40 bg-gray-300 rounded animate-pulse" />
          </div>

          {/* Profile Skeleton */}
          <div class="bg-white rounded-lg shadow-lg overflow-hidden mb-6 animate-pulse">
            <div class="md:flex">
              <div class="h-64 w-full md:w-64 bg-gray-300" />
              <div class="p-8 flex-1 space-y-4">
                <div class="h-8 bg-gray-300 rounded w-2/3" />
                <div class="h-6 bg-gray-200 rounded w-1/2" />
                <div class="h-4 bg-gray-200 rounded w-1/3" />
                <div class="h-4 bg-gray-200 rounded w-1/4" />
              </div>
            </div>
          </div>

          {/* Content Skeletons */}
          <div class="space-y-6">
            <div class="bg-white rounded-lg shadow-md p-6 animate-pulse">
              <div class="h-6 bg-gray-300 rounded w-1/4 mb-4" />
              <div class="space-y-2">
                <div class="h-4 bg-gray-200 rounded w-full" />
                <div class="h-4 bg-gray-200 rounded w-5/6" />
                <div class="h-4 bg-gray-200 rounded w-4/6" />
              </div>
            </div>
            <div class="bg-white rounded-lg shadow-md p-6 animate-pulse">
              <div class="h-6 bg-gray-300 rounded w-1/3 mb-4" />
              <div class="space-y-2">
                <div class="h-4 bg-gray-200 rounded w-full" />
                <div class="h-4 bg-gray-200 rounded w-5/6" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div class="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div class="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              class="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">Error al cargar</h2>
          <p class="text-gray-600 mb-6">{error}</p>
          <button
            onClick={goBack}
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Volver al directorio
          </button>
        </div>
      </div>
    );
  }

  // Success state - show instructor profile
  if (!instructor) {
    return null;
  }

  return (
    <div class="min-h-screen bg-gray-50 py-8">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={goBack}
          class="mb-6 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition"
        >
          <svg
            class="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Volver al directorio
        </button>

        {/* Breadcrumbs */}
        <nav class="mb-4 text-sm" aria-label="Breadcrumb">
          <ol class="flex items-center space-x-2 text-gray-500">
            <li>
              <a href="/" class="hover:text-blue-600 transition">
                Directorio
              </a>
            </li>
            <li>
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
            </li>
            <li class="text-gray-900 font-medium">
              {instructor.basic_info.name}
            </li>
          </ol>
        </nav>

        {/* Instructor Profile */}
        <InstructorProfileComponent instructor={instructor} />
      </div>
    </div>
  );
}
